import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ListTodo, Plus, CalendarDays, RefreshCw, Building2, AlertTriangle, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TaskFilters } from "@/components/tasks/TaskFilters";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { TaskCalendar } from "@/components/tasks/TaskCalendar";
import { TasksByCustomer } from "@/components/tasks/TasksByCustomer";
import { TaskBoard } from "@/components/tasks/TaskBoard";
import { MobileFab } from "@/components/MobileFab";
import { CalendarEventDialog } from "@/components/tasks/CalendarEventDialog";
import { ScrollFade } from "@/components/ScrollFade";
import { useTasks } from "@/hooks/useTasks";

export default function Tasks({ embedded = false }) {
  const t = useTasks();
  const {
    calendar, activeTab, setActiveTab, users, customers, loading, syncing,
    dialogOpen, setDialogOpen, deleteDialogOpen, setDeleteDialogOpen, taskToDelete,
    editTask, saving, myDayIds, formData, setFormData,
    filterStatus, setFilterStatus, filterPriority, setFilterPriority,
    filterAssignee, setFilterAssignee, filterCustomer, setFilterCustomer,
    tasks, tasksByStatus, openEditDialog, openCreateDialog,
    handleSubmit, handleDelete, handleStatusChange, handleSyncFromExchange,
    handleToggleMyDay, handlePlanWeek, requestDelete,
    overdueOnly, setOverdueOnly,
  } = t;

  const filterProps = {
    filterStatus, onFilterStatusChange: setFilterStatus,
    filterPriority, onFilterPriorityChange: setFilterPriority,
    filterAssignee, onFilterAssigneeChange: setFilterAssignee,
    filterCustomer, onFilterCustomerChange: setFilterCustomer,
    users, customers, taskCount: tasks.length,
  };
  const listProps = {
    onEdit: openEditDialog,
    onDelete: requestDelete,
    onStatusChange: handleStatusChange,
    myDayIds,
    onToggleMyDay: handleToggleMyDay,
    onPlanWeek: handlePlanWeek,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MobileFab onClick={openCreateDialog} label="Neue Aufgabe" testid="fab-new-task" />
      {/* Header */}
      {embedded ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={handleSyncFromExchange} disabled={syncing} data-testid="sync-exchange-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Synchronisiere..." : "Mit Exchange synchronisieren"}
          </Button>
        </div>
      ) : (
        <div className="brand-hero rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Aufgaben</h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Aufgaben und synchronisieren Sie mit Exchange
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleSyncFromExchange} disabled={syncing} data-testid="sync-exchange-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Synchronisiere..." : "Mit Exchange synchronisieren"}
            </Button>
            <Button onClick={openCreateDialog} data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Aufgabe
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {overdueOnly && (
          <div className="mb-4 flex items-center gap-2" data-testid="overdue-filter-bar">
            <Badge className="bg-warning/10 text-warning border-warning/20 hover:bg-warning/10 gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Nur überfällige Aufgaben
              <button onClick={() => setOverdueOnly(false)} className="ml-1 hover:text-foreground" data-testid="overdue-filter-clear" aria-label="Filter entfernen">
                <X className="w-3.5 h-3.5" />
              </button>
            </Badge>
          </div>
        )}
        <ScrollFade>
          <TabsList className="w-max max-w-none overflow-x-visible">
            <TabsTrigger value="board" data-testid="tab-board">
              <ListTodo className="w-4 h-4 mr-2" />
              Board
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">
              <CalendarDays className="w-4 h-4 mr-2" />
              Kalender
            </TabsTrigger>
            <TabsTrigger value="by-customer" data-testid="tab-by-customer">
              <Building2 className="w-4 h-4 mr-2" />
              Nach Kunde
            </TabsTrigger>
          </TabsList>
        </ScrollFade>

        {/* Board Tab */}
        <TabsContent value="board" className="space-y-6">
          <TaskFilters {...filterProps} />
          <TaskBoard tasksByStatus={tasksByStatus} {...listProps} />
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <TaskCalendar
            calendarData={calendar.calendarData}
            calendarMonth={calendar.calendarMonth}
            setCalendarMonth={calendar.setCalendarMonth}
            loading={calendar.calendarLoading}
            onCreateEvent={calendar.openCreateEventDialog}
            onEventClick={calendar.handleEventClick}
          />
        </TabsContent>

        {/* By Customer Tab */}
        <TabsContent value="by-customer" className="space-y-6">
          <TaskFilters {...filterProps} />
          <TasksByCustomer tasks={tasks} {...listProps} />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Task Dialog */}
      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editTask={editTask}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        saving={saving}
        users={users}
        customers={customers}
        onPlanWeek={handlePlanWeek}
      />

      {/* Create/Edit Calendar Event Dialog */}
      <CalendarEventDialog
        open={calendar.eventDialogOpen}
        onOpenChange={(open) => {
          calendar.setEventDialogOpen(open);
          if (!open) calendar.setSelectedEvent(null);
        }}
        onSubmit={calendar.handleEventSubmit}
        onDelete={calendar.handleDeleteCalendarEvent}
        saving={calendar.savingEvent}
        deleting={calendar.deletingEvent}
        editEvent={calendar.selectedEvent}
        calendarOwners={calendar.calendarOwners}
        defaultCalendarOwner={calendar.exchangeEmail}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie &quot;{taskToDelete?.title}&quot; wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-task-btn"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
